import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import AthletesTab from './AthletesTab';
import CalendarAgendaView from './CalendarAgendaView';
import ProgramCalendarMonthView from './ProgramCalendarMonthView';
import ProgramCalendarNavigationButton from './ProgramCalendarNavigationButton';
import ProgramCalendarWeekView from './ProgramCalendarWeekView';
import { FilterTerms, ProgramCalendarView, ProgrammingNavState, ProgrammingTab } from './types';

export interface ProgramCalendarProps {
  searchFilters: FilterTerms;
  permission?: boolean;
  activeTab?: ProgrammingTab;
}

const ProgramCalendar = ({ searchFilters, activeTab = 'classes' }: ProgramCalendarProps): JSX.Element => {
  const location = useLocation();
  const navState = location.state as ProgrammingNavState | null;

  const [view, setView] = useState<ProgramCalendarView>(navState?.view ?? 'week');

  const calendarMaxDate = dayjs(new Date()).add(1, 'year').toDate();
  const [date, setDate] = useState(() =>
    navState?.date ? dayjs(navState.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
  );

  useEffect(() => {
    const nav = location.state as ProgrammingNavState | null;
    if (nav?.view) {
      setView(nav.view);
    }
    if (nav?.date) {
      setDate(dayjs(nav.date).format('YYYY-MM-DD'));
    }
  }, [location.state]);

  interface NavDateProps {
    selectedDate?: Date;
    onDateSelect?: (date: Date) => void;
  }

  const renderProgramCalendarNavigationButtons = (navProps?: NavDateProps): JSX.Element => (
    <ProgramCalendarNavigationButton view={view} setView={setView} {...navProps} />
  );

  if (activeTab === 'athletes') {
    return (
      <AthletesTab
        initialAthleteId={navState?.athleteId}
        view={view}
        setView={setView}
        date={date}
        setDate={setDate}
        calendarMaxDate={calendarMaxDate}
      />
    );
  }

  return (
    <>
      {view === 'week' && (
        <ProgramCalendarWeekView
          date={date}
          setDate={setDate}
          setView={setView}
          searchFilters={searchFilters}
        />
      )}
      {view === 'month' && (
        <ProgramCalendarMonthView
          programCalendarNavigationButtons={renderProgramCalendarNavigationButtons}
          setDate={setDate}
          setView={setView}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
        />
      )}
      {view === 'agenda' && (
        <CalendarAgendaView
          programCalendarNavigationButtons={renderProgramCalendarNavigationButtons}
          date={date}
          setDate={setDate}
          searchFilters={searchFilters}
          calendarMaxDate={calendarMaxDate}
        />
      )}
    </>
  );
};

export default ProgramCalendar;
